import React, { useEffect, useState, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks/storehooks'
import useWindowDimensions from '@/hooks/Dimensions/useWindowDimensions'
import { Button } from '@/components/ui/button/button'
import {
  ExpandLogoDark,
  SideBarCollapseArrow,
  CollapsedLogoDark,
  CollapsedLogoBase,
  GradientTechnNW,
  GradientOrderIcon,
  GradientAnalyticsReport,
  GradientUserGroups,
  GradientBillingUsage,
  GradientSupport,
} from '@/assets/svgIcons'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import Xoff from '@/assets/icons/x-off.svg'
import Profile from '@/designs/new/containers/Profile/Profile'
import {
  changeMenuStatus,
  setOverView,
} from '@/redux-store/slices/menuItemSlice/menuItemSlice'

import whitelogo from '@/assets/Pictures/layer1.svg'

import TechChoiceLogo from '@/assets/Pictures/tech-choice.png'
import TechChoiceDarkLogo from '@/assets/Pictures/techchoiceexpandeddark.svg'
import TechChoiceMini from '@/assets/Pictures/techchoicellc_mini_logo.jpg'
import TechChoiceMiniDark from '@/assets/Pictures/techchoicesmalldark.svg'
import { useAuth0 } from '@auth0/auth0-react'
import { fetchAssets } from '@/services/apis/api'
import { AxiosResponse } from 'axios'
import {
  setLogoData,
  setLoading as setLogoReduxLoading,
} from '@/redux-store/slices/logoSlice/logoSlice'
import { GradientHome } from '@/assets/icons/GradientHome'
import { GlobalKnowledge } from '@/assets/icons/GlobalKnowledge'
import DependentDropdowns from '../header/dynamicDropdown'
import GradiantSkelton from '../../components/atom/gradientSkelton/gradientSkelton'
import { useWebTour } from '@/designs/new/contexts/WebTourContext'

export type LogoApiResponse = {
  showCustomLogo: boolean
  organizationId: string
  images: {
    logoMiniLight: string
    logoLargeLight: string
    logoMiniDark: string
    logoLargeDark: string
  }
}

export type FetchEventsResponse = AxiosResponse<{ data: LogoApiResponse }>

type IconMapping = {
  [key: string]: React.ReactNode
}

const iconMapping: IconMapping = {
  Home: <GradientHome />,
  'Technology & Network': <GradientTechnNW />,
  'Analytics & Reports': <GradientAnalyticsReport />,
  'Users & Groups': <GradientUserGroups />,
  'Billing & Usage': <GradientBillingUsage />,
  Support: <GradientSupport />,
  'Service Delivery': <GradientOrderIcon />,
  Wiki: <GlobalKnowledge />,
  'AI Assistant': <GlobalKnowledge />,
}

const SmoothLogo: React.FC<{
  dark: React.ReactNode
  light: React.ReactNode
  isDark: boolean
  className?: string
}> = ({ dark, light, isDark, className = '' }) => {
  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      <div
        className={`transition-opacity duration-150 ease-out flex items-center justify-center h-full w-full ${
          isDark ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {dark}
      </div>
      <div
        className={`absolute inset-0 transition-opacity duration-150 ease-out flex items-center justify-center h-full w-full ${
          isDark ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {light}
      </div>
    </div>
  )
}

const Sidebar: React.FC = () => {
  const { logout } = useAuth0()
  const location = useLocation()
  const { width } = useWindowDimensions()
  const dispatch = useAppDispatch()
  const { isTourRunning } = useWebTour()
  const menus = useAppSelector(state =>
    state.menu.items.filter(item => item.id !== 'settings'),
  )
  const [selectedMenu, setSelectedMenu] = useState(() => {
    const homeMenu = menus.find(item => item.name === 'Home')
    return homeMenu ? homeMenu.path : menus[0]?.path
  })
  const [openItem, setOpenItem] = useState<string[]>([''])
  const logoState = useAppSelector(state => state.logo)
  const show = logoState.logoShow
  const logoImages = logoState.images
  const loading = logoState.loading

  const logoMini = logoImages?.logoMiniLight || ''
  const logoLarge = logoImages?.logoLargeLight || ''
  const logoMiniDark = logoImages?.logoMiniDark || ''
  const logoLargeDark = logoImages?.logoLargeDark || ''
  const mapIndex = useAppSelector(state => state.menu.isMap)
  const themeValue = useAppSelector(state => state.menu.value)
  const menuExpandedFromStore = useAppSelector(state => state.menu.menuExpanded)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const isSpectrotel = !show

  const isDarkTheme =
    themeValue === 'dark' || themeValue === 'high-contrast-dark'

  const logoContent = isSpectrotel ? (
    <SmoothLogo
      isDark={isDarkTheme}
      className='w-[107px] h-[55px]'
      dark={<ExpandLogoDark />}
      light={
        <img
          src={whitelogo}
          alt='logo'
          className='w-full h-full object-contain'
        />
      }
    />
  ) : (
    <SmoothLogo
      isDark={isDarkTheme}
      className='w-[150px] h-[55px]'
      dark={
        <img
          src={logoLargeDark || TechChoiceDarkLogo}
          alt='TechChoice logo'
          className='w-full h-full object-contain'
        />
      }
      light={
        <img
          src={logoLarge || TechChoiceLogo}
          alt='Logo'
          className='w-full h-full object-contain'
        />
      }
    />
  )

  const miniLogoContent = isSpectrotel ? (
    <SmoothLogo
      isDark={isDarkTheme}
      className='w-10 h-10 left-2'
      dark={<CollapsedLogoDark />}
      light={<CollapsedLogoBase />}
    />
  ) : (
    <SmoothLogo
      isDark={isDarkTheme}
      className='w-10 h-10 left-2'
      dark={
        <img
          src={logoMiniDark || TechChoiceMiniDark}
          alt='TechChoice mini logo'
          className='w-full h-full object-contain'
        />
      }
      light={
        <img
          src={logoMini || TechChoiceMini}
          alt='TechChoice mini logo'
          className='w-full h-full object-contain'
        />
      }
    />
  )

  const [menuExpanded, setMenuExpanded] = useState(false)
  const shouldCollapse = width <= 1625

  useEffect(() => {
    if (shouldCollapse && menuExpanded) {
      setMenuExpanded(false)
      dispatch(changeMenuStatus(!menuExpandedFromStore))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCollapse])

  useEffect(() => {
    setSelectedMenu(location.pathname)

    const menuMapping: Record<string, string> = {
      technologyandnetworks: 'Technology & Network',
      analyticsandreports: 'Analytics & Reports',
      userandgroups: 'Users & Groups',
      support: 'Support',
      billingandusage: 'Billing & Usage',
    }

    const menuKey = Object.keys(menuMapping).find(key =>
      location.pathname.includes(key),
    ) as keyof typeof menuMapping

    if (menuKey) {
      setOpenItem([menuMapping[menuKey]])
    }
  }, [location.pathname])

  const handleMenuStatus = () => {
    setMenuExpanded(prev => !prev)
    dispatch(changeMenuStatus(!menuExpandedFromStore))
  }

  const handleHomeClick = () => {
    dispatch(setOverView('Home'))
  }

  const handleMenuItemClick = (path: string) => {
    setSelectedMenu(path)
    if (path === '/home' || path === '/dashboard') {
      handleHomeClick()
    }

    if (width < 640) {
      setMenuExpanded(false)
      dispatch(changeMenuStatus(false))
    }
  }

  useEffect(() => {
    setMenuExpanded(menuExpandedFromStore)
  }, [menuExpandedFromStore])

  // Ensure sidebar is expanded when tour is running
  useEffect(() => {
    if (isTourRunning && !menuExpanded && width >= 640) {
      setMenuExpanded(true)
      dispatch(changeMenuStatus(true))
    }
  }, [isTourRunning, menuExpanded, width, dispatch])

  useEffect(() => {
    if (width < 640) {
      setMenuExpanded(false)
      dispatch(changeMenuStatus(false))
    }
  }, [width, dispatch])

  // Close sidebar when clicking outside on mobile screens
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only apply this behavior on mobile screens (width < 768)
      if (width >= 640) return

      // Check if sidebar is open and click is outside the sidebar
      if (
        menuExpanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setMenuExpanded(false)
        dispatch(changeMenuStatus(false))
      }
    }

    // Add event listener when sidebar is open on mobile
    if (menuExpanded && width < 640) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuExpanded, width, dispatch])

  const fetchShoworNot = async () => {
    if (logoImages) return

    dispatch(setLogoReduxLoading(true))

    try {
      const res = (await fetchAssets()) as FetchEventsResponse
      if (res.status === 200) {
        dispatch(
          setLogoData({
            logoShow: res.data.data.showCustomLogo,
            images: res.data.data.images,
          }),
        )
      }
    } catch (error) {
      console.error('Failed to fetch logo data', error)
      dispatch(setLogoReduxLoading(false))
    }
  }

  useEffect(() => {
    fetchShoworNot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={`bg-elevated relative md:flex h-full flex-col transition-[width] flex-shrink-0 overflow-visible duration-150 ease-out will-change-[width]
    ${menuExpanded ? 'w-0 md:w-[300px]' : 'w-0 md:w-[70px]'}
  `}
    >
      {/* Expanded sidebar content - always mounted on mobile, visibility controlled by CSS */}
      <div
        data-sidebaropen={menuExpanded}
        className={`bg-elevated absolute top-0 left-0 w-[300px] h-full z-50 flex flex-col gap-padding-xl p-padding-xl transition-[transform,opacity] duration-150 ease-out data-[sidebaropen=true]:opacity-100 data-[sidebaropen=true]:translate-x-0 data-[sidebaropen=false]:opacity-0 data-[sidebaropen=false]:-translate-x-full data-[sidebaropen=false]:pointer-events-none will-change-transform transform-gpu`}
      >
        {/* for mobile */}
        <div className='lg:hidden flex px-padding-3xl p-padding-sm text-xl font-bold justify-between items-center border-[1px] border-border rounded-radius-sm'>
          {!loading ? (
            width < 640 ? (
              <Profile />
            ) : (
              <Link to={'/home'} onClick={handleHomeClick}>
                {logoContent}
              </Link>
            )
          ) : (
            <GradiantSkelton className='w-32 h-8 rounded-radius-sm' />
          )}
          <div
            className='w-5 h-5 flex justify-center items-center rounded-radius-full bg-primary-foreground flex-shrink-0 cursor-pointer hover:scale-110 transition-transform'
            onClick={handleMenuStatus}
          >
            <img src={Xoff} alt='x-off btn' className='h-14 w-14' />
          </div>
        </div>
        {width < 640 && (
          <div className='lg:hidden'>
            <DependentDropdowns />
            <hr className='mt-4' />
          </div>
        )}

        {/* for desk */}
        <div className='hidden lg:flex bg-elevated px-padding-3xl p-padding-sm text-xl font-bold justify-between items-center content-center border-[1px] border-border rounded-radius-sm'>
          {!loading ? (
            <Link to={'/home'} onClick={handleHomeClick}>
              {logoContent}
            </Link>
          ) : (
            <GradiantSkelton className='w-32 h-8 rounded-radius-sm' />
          )}
          <div
            className='w-5 h-5 flex justify-center items-center rounded-radius-full bg-primary-foreground flex-shrink-0 cursor-pointer hover:scale-110 transition-transform'
            onClick={handleMenuStatus}
          >
            <SideBarCollapseArrow />
          </div>
        </div>

        <div
          className={`flex-1 ${width < 640 ? 'h-[65vh]' : 'h-[85vh]'} overflow-y-auto spec-scroll`}
        >
          <Accordion
            type='multiple'
            value={openItem}
            onValueChange={setOpenItem}
          >
            {menus?.map(item => {
              const iconSrc = iconMapping[item?.name] || ''
              return item.children.length > 0 ? (
                <AccordionItem key={item.id} value={item.name}>
                  <AccordionTrigger
                    className='p-padding-xl border-l-[4px] border-none'
                    data-testid={`test-accordion-item-${item.name}`}
                  >
                    <div
                      data-tour={item.name}
                      className={`flex gap-padding-md justify-end items-center text-primary-foreground ${width < 640 ? 'text-[16px]' : 'text-sidebar-menu'}`}
                    >
                      {iconSrc}
                      {menuExpanded ? item.name : ''}{' '}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`test-accordion-content-${item.name}`}
                  >
                    {item.children.map(child => (
                      <div
                        key={child.path}
                        className='transition-[transform,opacity] duration-150 ease-out'
                      >
                        <Link
                          data-testid={`test-link-${child.label}`}
                          data-tour={child.label}
                          to={child.path}
                          aria-current={
                            child.path === selectedMenu ? 'page' : undefined
                          }
                          className={`py-padding-xl pr-padding-xl pl-11 rounded-radius-sm flex border-l-[4px] border-transparent ${width < 640 ? 'text-[16px]' : 'text-sidebar-submenu'} items-center hover:bg-primary-active hover:border-[#border-[#FB0E00]] ${
                            selectedMenu.startsWith(child.path)
                              ? 'bg-primary-active font-semibold text-primary-active-foreground border-l-[4px] border-l-red'
                              : 'text-primary-foreground'
                          }`}
                          onClick={() => handleMenuItemClick(child.path)}
                        >
                          {child.label}
                        </Link>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ) : (
                <div key={item.id}>
                  <Link
                    data-testid={`test-link-${item.name}`}
                    to={item.path}
                    onClick={() => handleMenuItemClick(item.path)}
                    aria-current={
                      item.path === selectedMenu ? 'page' : undefined
                    }
                    className={`rounded-radius-sm flex items-center h-[2.75rem]  border-transparent ${width < 640 ? 'text-[16px]' : 'text-sidebar-menu'} px-padding-xl hover:bg-primary-active transition-[transform,opacity] duration-150 ease-out hover:border-[#border-[#FB0E00]] ${
                      selectedMenu.startsWith(item.path)
                        ? 'bg-primary-active font-semibold text-primary-active-foreground border-l-[4px] border-l-red'
                        : 'text-primary-foreground'
                    }`}
                  >
                    <div className='mr-2'>{iconSrc}</div>
                    <span
                      className={`
                            overflow-hidden
                            whitespace-nowrap
                            transition-[transform,opacity]
                            duration-150 ease-out
                            ${
                              menuExpanded
                                ? 'opacity-100 max-w-[200px]'
                                : 'opacity-0 max-w-0 ml-0'
                            }
                      `}
                    >
                      {item.name}
                    </span>
                  </Link>
                </div>
              )
            })}
          </Accordion>
        </div>
        {width < 640 && (
          <div className='flex p-[12px] justify-between items-center shrink-0 border-t-[1px] border-secondary-border'>
            <div
              className='flex items-center gap-[8px] text-danger'
              onClick={() =>
                logout({
                  logoutParams: { returnTo: `${window.location.origin}/app` },
                })
              }
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M19.8531 12.3531C20.0469 12.1594 20.0469 11.8406 19.8531 11.6469L15.8531 7.64687C15.6594 7.45312 15.3406 7.45312 15.1469 7.64687C14.9531 7.84062 14.9531 8.15938 15.1469 8.35313L18.2937 11.5H9.5C9.225 11.5 9 11.725 9 12C9 12.275 9.225 12.5 9.5 12.5H18.2937L15.1469 15.6469C14.9531 15.8406 14.9531 16.1594 15.1469 16.3531C15.3406 16.5469 15.6594 16.5469 15.8531 16.3531L19.8531 12.3531ZM9.5 6C9.775 6 10 5.775 10 5.5C10 5.225 9.775 5 9.5 5H6.5C5.11875 5 4 6.11875 4 7.5V16.5C4 17.8813 5.11875 19 6.5 19H9.5C9.775 19 10 18.775 10 18.5C10 18.225 9.775 18 9.5 18H6.5C5.67188 18 5 17.3281 5 16.5V7.5C5 6.67188 5.67188 6 6.5 6H9.5Z'
                  fill='#DA586A'
                />
              </svg>
              <h1 className='text-[16px] font-normal'>Logout</h1>
            </div>
          </div>
        )}
      </div>

      {/* Collapsed sidebar content */}
      <div
        data-sidebaropen={menuExpanded}
        className={`hidden md:flex flex-col absolute top-0 left-0 w-[70px] h-full transition-[transform,opacity] duration-150 ease-out data-[sidebaropen=true]:opacity-0 data-[sidebaropen=true]:pointer-events-none data-[sidebaropen=true]:translate-x-full data-[sidebaropen=false]:opacity-100 data-[sidebaropen=false]:translate-x-0 data-[sidebaropen=false]:pointer-events-auto will-change-transform transform-gpu`}
      >
        <div
          className={`relative h-[71px] py-padding-md text-xl font-bold gap-2 border-b border-b-border-tertiary items-center flex flex-row justify-between ${mapIndex === true ? '' : 'z-50'}`}
        >
          <Link to={'/home'} onClick={handleHomeClick}>
            {miniLogoContent}
          </Link>
          <div
            className={`w-[20px] h-[20px] ${mapIndex === true ? '' : 'z-[60]'} flex flex-shrink-0 justify-center items-center cursor-pointer bg-primary-foreground rounded-radius-full rotate-180 hover:scale-110 transition-transform absolute -right-2`}
            onClick={handleMenuStatus}
          >
            <SideBarCollapseArrow />
          </div>
        </div>
        <nav className='flex-1 space-y-4'>
          {menus.map(item => {
            const iconSrc = iconMapping[item?.name] || ''

            return (
              <div
                key={item.id}
                className='transition-[transform,opacity] duration-150 ease-out py-padding-md w-full flex justify-center !m-0'
              >
                <div className='relative group w-fit'>
                  <Button
                    to={item.path}
                    onClick={handleMenuStatus}
                    className='w-11 h-11 flex shrink-0 justify-center cursor-pointer items-center rounded-radius-sm bg-info-dim-light'
                    aria-label={item.name}
                  >
                    {iconSrc}
                  </Button>
                  <span
                    className='
                        absolute left-full ml-2 top-1/2 -translate-y-1/2  
                        bg-primary text-primary-foreground                  
                        px-3 py-2 rounded-radius-md shadow-lg
                        opacity-0 group-hover:opacity-100
                        transition-opacity whitespace-nowrap z-50 pointer-events-none
                        text-sm font-medium
                    '
                  >
                    {item.name}
                  </span>
                </div>
              </div>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

export default Sidebar
